import React, { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

// Marketing
import Landing from "./components/Landing";
import LandingPreviewIndex from "./components/landings/LandingPreviewIndex";
import LandingEditorial from "./components/landings/LandingEditorial";
import LandingProduct from "./components/landings/LandingProduct";
import LandingPlayful from "./components/landings/LandingPlayful";
import LandingGuided from "./components/landings/LandingGuided";
import HowItWorks from "./components/HowItWorks";
import Pricing from "./components/Pricing";
import Contact from "./components/Contact";
import PublicCard from "./components/PublicCard";
import PublicCardById from "./components/PublicCardById";
import PublicCardForm from "./components/PublicCardForm";
import TermsOfService from "./components/TermsOfService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import UseCaseRecruiters from "./components/UseCaseRecruiters";
import UseCaseSales from "./components/UseCaseSales";
import UseCaseRealEstate from "./components/UseCaseRealEstate";
import UseCaseConsultants from "./components/UseCaseConsultants";
import UseCaseFounders from "./components/UseCaseFounders";
import Teams from "./components/Teams";
import Help from "./components/Help";
import Trust from "./components/Trust";
import CompareWhoBrewVsPopl from "./components/compare/CompareWhoBrewVsPopl";
import CompareWhoBrewVsBlinq from "./components/compare/CompareWhoBrewVsBlinq";
import CompareWhoBrewVsLinq from "./components/compare/CompareWhoBrewVsLinq";
import CompareWhoBrewVsV1ce from "./components/compare/CompareWhoBrewVsV1ce";

// App
import MyCard from "./components/MyCard";
import OrderCard from "./components/OrderCard";
import Profile from "./components/Profile";
import OrderConfirmation from "./components/OrderConfirmation";
import NotFound from "./components/NotFound";
import Insights from "./components/Insights";
import Admin from "./components/Admin";
import Leads from "./components/Leads";
import EmailSignature from "./components/EmailSignature";
import VenueOnboarding from "./components/venue/VenueOnboarding";
import VenueDashboard from "./components/venue/VenueDashboard";
import ValentSignature from "./components/ValentSignature";

// Layouts
import PublicLayout from "./components/layouts/PublicLayout";
import AppLayout from "./components/layouts/AppLayout";

// Shell
import AuthSync from "./components/AuthSync";
import ScrollToTop from "./components/ScrollToTop";
import Onboarding from "./components/Onboarding";
import CommandPalette from "./components/CommandPalette";
import CookieConsent from "./components/CookieConsent";
import { ToastProvider } from "./components/Toast";

const GuestLayout = React.lazy(() => import("./components/layouts/GuestLayout"));
const GuestExperience = React.lazy(() => import("./components/guest/GuestExperience"));

const GuestSpinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-cream-50 dark:bg-coffee-900">
    <div className="animate-spin rounded-full h-8 w-8 border-2 border-coffee-900 dark:border-cream-50 border-t-transparent" />
  </div>
);

function App() {
  return (
    <Router>
      <ToastProvider>
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-coffee-900 focus:text-cream-50 focus:rounded"
        >
          Skip to main content
        </a>
        <AuthSync />
        <ScrollToTop />
        <Onboarding />
        <CommandPalette />

        <Routes>
          {/* Public — marketing & legal */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/teams" element={<Teams />} />
            <Route path="/help" element={<Help />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/trust" element={<Trust />} />

            <Route path="/for/recruiters"   element={<UseCaseRecruiters />} />
            <Route path="/for/sales"        element={<UseCaseSales />} />
            <Route path="/for/real-estate"  element={<UseCaseRealEstate />} />
            <Route path="/for/consultants"  element={<UseCaseConsultants />} />
            <Route path="/for/founders"     element={<UseCaseFounders />} />

            <Route path="/compare/whobrew-vs-popl"  element={<CompareWhoBrewVsPopl />} />
            <Route path="/compare/whobrew-vs-blinq" element={<CompareWhoBrewVsBlinq />} />
            <Route path="/compare/whobrew-vs-linq"  element={<CompareWhoBrewVsLinq />} />
            <Route path="/compare/whobrew-vs-v1ce"  element={<CompareWhoBrewVsV1ce />} />

            {/* Specific-card view by id (D-001) — before the handle route so
                the literal "id" segment is not read as a handle. */}
            <Route path="/card/id/:cardId" element={<PublicCardById />} />
            <Route path="/card/:username" element={<PublicCard />} />
            <Route path="/card/:username/form/:linkId" element={<PublicCardForm />} />
            <Route path="/terms"          element={<TermsOfService />} />
            <Route path="/privacy"        element={<PrivacyPolicy />} />

            {/* Standalone tool — public chrome, no account */}
            <Route path="/valent"         element={<ValentSignature />} />

            {/* Internal/preview — kept reachable but noindexed via SEO config inside */}
            <Route path="/preview"   element={<LandingPreviewIndex />} />
            <Route path="/preview/a" element={<LandingEditorial />} />
            <Route path="/preview/b" element={<LandingProduct />} />
            <Route path="/preview/c" element={<LandingPlayful />} />
            <Route path="/preview/d" element={<LandingGuided />} />
          </Route>

          {/* Authenticated app */}
          <Route element={<AppLayout />}>
            <Route path="/my-card"             element={<MyCard />} />
            <Route path="/insights"            element={<Insights />} />
            <Route path="/leads"               element={<Leads />} />
            <Route path="/email-signature"     element={<EmailSignature />} />
            <Route path="/profile"             element={<Profile />} />
            <Route path="/order-card"          element={<OrderCard />} />
            <Route path="/order-confirmation"  element={<OrderConfirmation />} />
            <Route path="/admin"               element={<Admin />} />
            <Route path="/venue/setup"         element={<VenueOnboarding />} />
            <Route path="/venue/dashboard"     element={<VenueDashboard />} />
          </Route>

          {/* Guest */}
          <Route
            element={
              <Suspense fallback={<GuestSpinner />}>
                <GuestLayout />
              </Suspense>
            }
          >
            <Route
              path="/v/:slug"
              element={
                <Suspense fallback={<GuestSpinner />}>
                  <GuestExperience />
                </Suspense>
              }
            />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>

        <CookieConsent />
      </ToastProvider>
    </Router>
  );
}

export default App;
